import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useI18n } from "context/I18nContext";
import { elevatedPumpRechargeRoles, normalizeRoleName, RoleName } from "constants/roles";
import styles from "./Sidebar.module.scss";
import NavItem from "./NavItem";

type SidebarChildItem = {
  key: string;
  to: string;
  end?: boolean;
};

type SidebarNavItem = {
  key: string;
  to: string;
  icon: string;
  end?: boolean;
  disabled?: boolean;
  children?: SidebarChildItem[];
};

type SubNavItemProps = {
  to: string;
  label: string;
  end?: boolean;
};

function SubNavItem({ to, label, end }: SubNavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        isActive ? `${styles.subItem} ${styles.subItemActive}` : styles.subItem
      }
    >
      <span className={styles.subBullet} aria-hidden="true" />
      <span className={styles.subLabel}>{label}</span>
    </NavLink>
  );
}

function Sidebar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const normalizedRoles = useMemo(
    () =>
      (user?.roles ?? [])
        .map((role) => normalizeRoleName(role.roleName))
        .filter((roleName): roleName is RoleName => Boolean(roleName)),
    [user]
  );
  const canAccessPumpRecharge = normalizedRoles.some((role) =>
    elevatedPumpRechargeRoles.includes(role)
  );
  const isSuperAdmin = normalizedRoles.includes(RoleName.SUPER_ADMIN);

  const toggleMenu = useCallback((key: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const navItems = useMemo<SidebarNavItem[]>(() => {
    const items: SidebarNavItem[] = [
      { to: "/", icon: "home", key: "home", end: true },
      { to: "/dashboard", icon: "dashboard", key: "dashboard" },
      { to: "/accounts", icon: "account", key: "accounts" },
      { to: "/recharge", icon: "recharge", key: "recharge" },
      { to: "/transactions/water", icon: "water", key: "waterTransaction" },
      { to: "/cards", icon: "card", key: "card" },
      { to: "/reports/daily", icon: "report", key: "reportsDaily", disabled: true },
      { to: "/transactions/bkash", icon: "bkash", key: "bkash", disabled: true },
      { to: "/transactions/upay", icon: "upay", key: "upay", disabled: true },
    ];

    const settingsChildren: SidebarChildItem[] = [];
    if (canAccessPumpRecharge) {
      settingsChildren.push({
        to: "/settings/pump-recharge",
        key: "settingsPumpRecharge",
      });
    }
    if (isSuperAdmin) {
      settingsChildren.push({
        to: "/settings/device-logs",
        key: "settingsDeviceLogs",
      });
    }
    if (settingsChildren.length > 0) {
      items.push({
        to: "/settings",
        icon: "setting",
        key: "settings",
        children: settingsChildren,
      });
    }

    items.push(
      { to: "/logs", icon: "log", key: "logs", disabled: true },
      { to: "/reports", icon: "report2", key: "reports", disabled: true }
    );
    return items;
  }, [canAccessPumpRecharge, isSuperAdmin]);

  useEffect(() => {
    setExpandedMenus((prev) => {
      let updated = false;
      const next = { ...prev };
      navItems.forEach((item) => {
        if (!item.children?.length) {
          return;
        }
        const shouldOpen = location.pathname.startsWith(item.to) ||
          item.children.some((child) => location.pathname.startsWith(child.to));
        if (shouldOpen && !next[item.key]) {
          next[item.key] = true;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [location.pathname, navItems]);
  const fullName =
    [user?.name, user?.surname].filter((part) => part?.trim()).join(" ") ||
    user?.userName ||
    t("sidebar.unknownUser");

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <div className={styles.logo}>
          <img
            src="/logo.png"
            alt="Dashboard"
            className={styles.logoImage}
            loading="lazy"
          />
        </div>
        <div className={styles.brandText}>
          <p className={styles.welcome}>{t("sidebar.welcome")}</p>
          <p className={styles.name}>{fullName}</p>
        </div>
      </div>

      <nav className={styles.nav}>
        {navItems.map(({ key, disabled, children, ...item }) => {
          const childItems = children ?? [];
          const isExpandable = childItems.length > 0 && !disabled;
          const isExpanded = isExpandable ? Boolean(expandedMenus[key]) : false;
          return (
            <React.Fragment key={key}>
              <NavItem
                label={t(`sidebar.${key}`)}
                disabled={disabled}
                comingSoonLabel={disabled ? t("sidebar.comingSoon") : undefined}
                onClick={isExpandable ? () => toggleMenu(key) : undefined}
                ariaExpanded={isExpandable ? isExpanded : undefined}
                rightIcon={isExpandable ? "chevronDown" : undefined}
                {...item}
              />
              {isExpandable && isExpanded && (
                <div className={styles.submenu}>
                  {childItems.map((child) => (
                    <SubNavItem
                      key={child.key}
                      to={child.to}
                      end={child.end}
                      label={t(`sidebar.${child.key}`)}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>
      <div className={styles.banner}>
        <p>{t("sidebar.banner")}</p>
      </div>
    </aside>
  );
}

export default Sidebar;
